import React, { Suspense, lazy, useCallback, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Footer from './components/Footer';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import Home from './pages/Home';
import './App.css';

const Services = lazy(() => import('./pages/Services'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const AccountingBookkeepingDubaiUAE = lazy(() => import('./pages/AccountingBookkeepingDubaiUAE'));
const CorporateServicesDubaiUAE = lazy(() => import('./pages/CorporateServicesDubaiUAE'));
const CompanySetupFreezoneDubaiUAE = lazy(() => import('./pages/CompanySetupFreezoneDubaiUAE'));
const TaxServicesDubaiUAE = lazy(() => import('./pages/TaxServicesDubaiUAE'));
const HRServicesDubaiUAE = lazy(() => import('./pages/HRServicesDubaiUAE'));
const ManagementConsultancyDubaiUAE = lazy(() => import('./pages/ManagementConsultancyDubaiUAE'));
const CorporateGovernanceComplianceDubaiUAE = lazy(() => import('./pages/CorporateGovernanceComplianceDubaiUAE'));
const LegalAssistanceDubaiUAE = lazy(() => import('./pages/LegalAssistanceDubaiUAE'));
const AuditAssistanceDubaiUAE = lazy(() => import('./pages/AuditAssistanceDubaiUAE'));
const BusinessCard = lazy(() => import('./pages/BusinessCard'));
const Newsroom = lazy(() => import('./pages/Newsroom'));
const NewsroomArticle = lazy(() => import('./pages/NewsroomArticle'));
const NewsroomAdmin = lazy(() => import('./pages/NewsroomAdmin'));
const NewsroomAdminLogin = lazy(() => import('./pages/NewsroomAdminLogin'));

const SECRET_NEWSROOM_LOGIN_PATH = process.env.REACT_APP_NEWSROOM_SECRET_LOGIN_PATH || '/sumit-editor-portal-7k29x';

const SEO_MAP = {
  '/': {
    title: 'Sumit Corporate Services | Corporate Services Provider in Dubai, UAE',
    description:
      'Expert business setup, company registration, accounting, tax, HR, and corporate advisory services in Dubai, UAE.',
    pageName: 'Home',
  },
  '/services': {
    title: 'Corporate Services in Dubai, UAE | Sumit Corporate Services',
    description:
      'Explore end-to-end corporate services including company formation, compliance, accounting, tax, HR, legal and audit support.',
    pageName: 'Services',
  },
  '/about': {
    title: 'About Sumit Corporate Services | Trusted UAE Business Advisors',
    description:
      'Learn about SUM-IT Corporate Services, our mission, and our expert team supporting UAE businesses with reliable corporate solutions.',
    pageName: 'About',
  },
  '/contact': {
    title: 'Contact Sumit Corporate Services | Get Expert Corporate Support',
    description:
      'Contact SUM-IT Corporate Services for business setup, accounting, tax, HR, legal, and compliance support in Dubai and UAE.',
    pageName: 'Contact',
  },
  '/accounting-bookkeeping-services-dubai-uae': {
    title: 'Accounting & Bookkeeping Services in Dubai, UAE | Sumit Corporate Services',
    description:
      'Professional accounting and bookkeeping services in Dubai, including reconciliations, reporting, payroll support, and audit readiness.',
    pageName: 'Accounting & Bookkeeping Services',
    serviceName: 'Accounting & Bookkeeping Services',
  },
  '/corporate-services-dubai-uae': {
    title: 'Corporate Services in Dubai, UAE | Bank Account & Advisory Support',
    description:
      'Corporate services support for UAE businesses, including bank account opening guidance, documentation support, and advisory assistance.',
    pageName: 'Corporate Services',
    serviceName: 'Corporate Services',
  },
  '/company-setup-freezone-dubai-uae': {
    title: 'Company Setup & Free Zone Business Formation in UAE | Sumit Corporate Services',
    description:
      'Set up your mainland or free zone company in UAE with end-to-end support for licensing, documentation, visas, and compliance.',
    pageName: 'Company Setup & Free Zone',
    serviceName: 'Company Setup & Free Zone Business Formation',
  },
  '/tax-services-dubai-uae': {
    title: 'Tax Services in Dubai, UAE | VAT & Corporate Tax Compliance',
    description:
      'Comprehensive UAE tax services including VAT, corporate tax registration, filings, compliance reviews, and FTA support.',
    pageName: 'Tax Services',
    serviceName: 'Tax Services',
  },
  '/hr-services-dubai-uae': {
    title: 'HR Services in Dubai, UAE | Payroll & Compliance Support',
    description:
      'Outsourced HR services in UAE including payroll support, documentation, policy setup, compliance checks, and workforce administration.',
    pageName: 'HR Services',
    serviceName: 'HR Services',
  },
  '/management-consultancy-dubai-uae': {
    title: 'Management Consultancy in Dubai, UAE | Strategy & Growth Advisory',
    description:
      'Business consultancy services for strategy, operational improvement, restructuring, and growth planning for UAE companies.',
    pageName: 'Management Consultancy',
    serviceName: 'Management Consultancy',
  },
  '/corporate-governance-compliance-dubai-uae': {
    title: 'Corporate Governance & Compliance in Dubai, UAE | Sumit Corporate Services',
    description:
      'Build strong governance frameworks with practical compliance support, controls, policy development, and risk management guidance.',
    pageName: 'Corporate Governance & Compliance',
    serviceName: 'Corporate Governance & Compliance Services',
  },
  '/legal-assistance-dubai-uae': {
    title: 'Legal Assistance in Dubai, UAE | Corporate Documentation Support',
    description:
      'Get legal assistance for contracts, MOA/AOA amendments, POA drafting, will support, and corporate documentation in UAE.',
    pageName: 'Legal Assistance',
    serviceName: 'Legal Assistance Services',
  },
  '/audit-assistance-dubai-uae': {
    title: 'Audit Assistance in Dubai, UAE | Financial Audit Readiness Support',
    description:
      'Prepare for audits with expert support for reconciliations, documentation, auditor coordination, and financial record readiness.',
    pageName: 'Audit Assistance',
    serviceName: 'Audit Assistance Services',
  },
  '/business-card': {
    title: 'SUM-IT Digital Business Card | Contact Sumit Corporate Services',
    description:
      'Access SUM-IT Corporate Services contact details, WhatsApp, email, and downloadable vCard from our digital business card page.',
    pageName: 'Business Card',
  },
  '/newsroom': {
    title: 'Newsroom | Sumit Corporate Services',
    description:
      'Read the latest announcements, corporate insights, and business updates from Sumit Corporate Services.',
    pageName: 'Newsroom',
  },
  '/newsroom/admin': {
    title: 'Newsroom Admin | Sumit Corporate Services',
    description:
      'Create, edit, schedule, and publish newsroom posts from the Sumit Corporate Services publishing console.',
    pageName: 'Newsroom Admin',
  },
  '/newsroom/article': {
    title: 'News Article | Sumit Corporate Services Newsroom',
    description:
      'Read full newsroom articles, insights, and official updates from Sumit Corporate Services.',
    pageName: 'News Article',
  },
  '/newsroom/login': {
    title: 'Newsroom Login | Sumit Corporate Services',
    description:
      'Private newsroom editorial login for publishing and managing Sumit Corporate Services articles.',
    pageName: 'Newsroom Login',
  },
};

const upsertMetaTag = (key, content, fallbackAttr = 'name') => {
  let element = document.querySelector(`meta[name="${key}"], meta[property="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(fallbackAttr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setCanonicalTag = (url) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
};

const buildStructuredData = (pathname, seo, origin) => {
  const pageUrl = `${origin}${pathname}`;
  const graph = [
    {
      '@type': 'WebPage',
      name: seo.pageName,
      url: pageUrl,
      description: seo.description,
      inLanguage: 'en',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Sumit Corporate Services',
        url: origin,
      },
    },
  ];

  if (pathname !== '/') {
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${origin}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: seo.pageName,
          item: pageUrl,
        },
      ],
    });
  }

  if (seo.serviceName) {
    graph.push({
      '@type': 'Service',
      name: seo.serviceName,
      description: seo.description,
      areaServed: ['Dubai', 'United Arab Emirates'],
      provider: {
        '@type': 'Organization',
        name: 'Sumit Corporate Services',
        url: origin,
      },
      url: pageUrl,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
};

const setStructuredData = (structuredData) => {
  const existing = document.getElementById('route-structured-data');
  if (existing) {
    existing.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'route-structured-data';
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

const AppRoutes = () => {
  const location = useLocation();
  const hideGlobalLayout = location.pathname === '/business-card';

  const removeEmergentBadge = useCallback(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.remove();
    }
  }, []);

  useEffect(() => {
    const isSecretLoginPath = location.pathname === SECRET_NEWSROOM_LOGIN_PATH;
    const seo = isSecretLoginPath
      ? SEO_MAP['/newsroom/login']
      : location.pathname.startsWith('/newsroom/') && location.pathname !== '/newsroom/admin'
        ? SEO_MAP['/newsroom/article']
        : SEO_MAP[location.pathname] || SEO_MAP['/'];
    const origin = window.location.origin;
    const pageUrl = `${origin}${location.pathname}`;
    const robotsContent = window.location.hostname.includes('preview.emergentagent.com')
      ? 'noindex,nofollow'
      : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

    document.title = seo.title;
    upsertMetaTag('description', seo.description, 'name');
    upsertMetaTag('og:title', seo.title, 'property');
    upsertMetaTag('og:description', seo.description, 'property');
    upsertMetaTag('og:type', 'website', 'property');
    upsertMetaTag('og:url', pageUrl, 'property');
    upsertMetaTag('og:image', `${origin}/sumit-home-logo-transparent.png`, 'property');
    upsertMetaTag('twitter:card', 'summary_large_image', 'name');
    upsertMetaTag('twitter:title', seo.title, 'name');
    upsertMetaTag('twitter:description', seo.description, 'name');
    upsertMetaTag('twitter:image', `${origin}/sumit-home-logo-transparent.png`, 'name');
    upsertMetaTag('robots', robotsContent, 'name');
    setCanonicalTag(pageUrl);
    setStructuredData(buildStructuredData(location.pathname, seo, origin));

    removeEmergentBadge();
    const observer = new MutationObserver(removeEmergentBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    const observerTimeoutId = window.setTimeout(() => {
      observer.disconnect();
    }, 3000);

    return () => {
      observer.disconnect();
      window.clearTimeout(observerTimeoutId);
    };
  }, [location.pathname, removeEmergentBadge]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <>
      <a
        href="#main-content"
        className="skip-link"
        data-testid="skip-to-content-link"
      >
        Skip to main content
      </a>
      {!hideGlobalLayout && <Header />}
      <main id="main-content" tabIndex={-1} className="min-h-screen" data-testid="main-content">
        <Suspense
          fallback={(
            <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="route-loading-state" aria-live="polite">
              Loading...
            </div>
          )}
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/accounting-bookkeeping-services-dubai-uae" element={<AccountingBookkeepingDubaiUAE />} />
            <Route path="/corporate-services-dubai-uae" element={<CorporateServicesDubaiUAE />} />
            <Route path="/company-setup-freezone-dubai-uae" element={<CompanySetupFreezoneDubaiUAE />} />
            <Route path="/tax-services-dubai-uae" element={<TaxServicesDubaiUAE />} />
            <Route path="/hr-services-dubai-uae" element={<HRServicesDubaiUAE />} />
            <Route path="/management-consultancy-dubai-uae" element={<ManagementConsultancyDubaiUAE />} />
            <Route path="/corporate-governance-compliance-dubai-uae" element={<CorporateGovernanceComplianceDubaiUAE />} />
            <Route path="/legal-assistance-dubai-uae" element={<LegalAssistanceDubaiUAE />} />
            <Route path="/audit-assistance-dubai-uae" element={<AuditAssistanceDubaiUAE />} />
            <Route path="/business-card" element={<BusinessCard />} />
            <Route path="/newsroom" element={<Newsroom />} />
            <Route path="/newsroom/admin" element={<NewsroomAdmin />} />
            <Route path="/newsroom/:slug" element={<NewsroomArticle />} />
            <Route path={SECRET_NEWSROOM_LOGIN_PATH} element={<NewsroomAdminLogin />} />
            <Route path="/about" element={<About />} />
            <Route path="/calculator" element={<Navigate to="/contact" replace />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </Suspense>
      </main>
      {!hideGlobalLayout && <FloatingWhatsApp />}
      {!hideGlobalLayout && <Footer />}
      <Toaster position="top-right" />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;